import { useState, useEffect, useCallback, useRef } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { useLazyGetProductsQuery } from '../../store/api/productsApi';

function ProductAutocomplete({ value, onChange, label = "Search Product", required = false, ...props }) {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState([]);
  const debounceTimer = useRef(null);

  const [triggerSearch, { data, isFetching }] = useLazyGetProductsQuery();

  const handleSearch = useCallback((q) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    
    if (!q) {
      setOptions([]);
      return;
    }

    debounceTimer.current = setTimeout(() => {
      triggerSearch({ search: q, limit: 20 });
    }, 500);
  }, [triggerSearch]);

  useEffect(() => {
    handleSearch(inputValue);
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
    };
  }, [inputValue, handleSearch]);

  useEffect(() => {
    if (data?.products) {
      setOptions(data.products);
    }
  }, [data]);

  const handleKeyDown = async (e) => {
    if (e.key === 'Enter') {
      // Prevent the scanner's 'Enter' from submitting the whole page's form
      e.preventDefault();
      e.stopPropagation();
      
      const currentValue = e.target.value;
      if (!currentValue) return;

      try {
        const result = await triggerSearch({ search: currentValue, limit: 1 }).unwrap();
        if (result?.products?.length > 0) {
          const product = result.products[0];
          // Auto-select if exact match on barcode or sku
          if (product.barcode === currentValue || product.sku === currentValue) {
            onChange(e, product, 'selectOption');
            setInputValue('');
            setOptions([]);
          }
        }
      } catch (err) {
        console.error('Barcode auto-search error:', err);
      }
    }
  };

  return (
    <Autocomplete
      {...props}
      options={options}
      loading={isFetching}
      value={value}
      onChange={onChange}
      inputValue={inputValue}
      onInputChange={(e, newInputValue, reason) => {
        if (reason === 'reset') {
          setInputValue('');
          setOptions([]);
        } else {
          setInputValue(newInputValue);
        }
      }}
      getOptionLabel={(option) => {
        if (!option) return '';
        const parts = [option.name];
        if (option.sku) parts.push(`[SKU: ${option.sku}]`);
        if (option.barcode) parts.push(`[Barcode: ${option.barcode}]`);
        if (option.brand) parts.push(`[Brand: ${option.brand}]`);
        return parts.join(' ');
      }}
      isOptionEqualToValue={(option, value) => option.id === value?.id}
      renderInput={(params) => (
        <TextField 
          {...params} 
          label={label} 
          required={required}
          onKeyDown={(e) => {
            if (params.inputProps.onKeyDown) {
              params.inputProps.onKeyDown(e);
            }
            handleKeyDown(e);
          }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isFetching ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

export default ProductAutocomplete;
